import React, { useState, useEffect, useLayoutEffect, useRef } from "react"
import {
  Drawer,
  Collapse,
  Input,
  Button,
  Spin,
  Form,
  message,
  Select,
} from "antd"
import cookie from "react-cookies"
import styled from "styled-components"
import {
  UserOutlined,
  DeleteOutlined,
  PlusOutlined,
  LoadingOutlined,
} from "@ant-design/icons"
import { withRouter } from "react-router-dom"
import ReferralDashboardContainer from "container/ReferralDashboard"
import agentSearchContainer from "container/BrokerDashboard/agentSearchContainer"
import GoogleAutoComplete from "dumbComponents/common/UI/antd/GoogleAutocomplete"
import {
  formatPhoneNumber,
  phoneDigits,
  defaultReferralContactErrors,
  validateReferralContactRows,
} from "dumbComponents/ReferralsDashboard/contactValidation"

const { Panel } = Collapse

const DrawerBody = styled.div`
  .ant-collapse {
    background: transparent;
    border: none;
  }

  .ant-collapse > .ant-collapse-item {
    border: 1px solid #e8e8f0;
    border-radius: 10px !important;
    margin-bottom: 16px;
    overflow: hidden;
  }

  .ant-collapse > .ant-collapse-item > .ant-collapse-header {
    font-size: 15px;
    font-weight: 600;
    color: #5b78f6;
    padding: 14px 20px;
    align-items: center;
  }

  .ant-collapse-content > .ant-collapse-content-box {
    padding: 0 20px 20px;
  }
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const SectionIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: #5b78f6;
  font-size: 18px;
`

const SectionIndicator = styled.div`
  width: 4px;
  height: 28px;
  background: #5b78f6;
  border-radius: 2px;
  margin-right: 4px;
`

const FieldRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 14px;
`

const FieldGroup = styled.div`
  flex: 1;
  min-width: 0;
`

const FieldLabel = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
  font-weight: 500;
`

const FullWidthField = styled.div`
  margin-bottom: 14px;
`

const SearchRow = styled.div`
  margin-bottom: 12px;
`

const InfoText = styled.div`
  font-size: 11px;
  color: #999;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 4px;
`

const RepAgentBlock = styled.div`
  padding: 16px;
  background: #fafafe;
  border: 1px solid #ececf4;
  border-radius: 8px;
  margin-bottom: 14px;
  position: relative;
`

const RepAgentTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
`

const RepAgentLabel = styled.span`
  font-weight: 600;
  font-size: 13px;
  color: #5b78f6;
`

const ClientBlock = styled.div`
  padding: 16px;
  background: #fafafe;
  border: 1px solid #ececf4;
  border-radius: 8px;
  margin-bottom: 14px;
  position: relative;
`

const ClientTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
`

const ClientLabel = styled.span`
  font-weight: 600;
  font-size: 13px;
  color: #5b78f6;
`

const AddButton = styled(Button)`
  color: #5b78f6;
  border-color: #5b78f6;
  font-size: 12px;
  &:hover {
    color: #3d5bd9;
    border-color: #3d5bd9;
  }
`

const SaveButton = styled(Button)`
  width: 280px;
  height: 44px;
  border-radius: 24px;
  font-size: 15px;
  font-weight: 600;
`

const FooterWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px 0 8px;
`

const DrawerTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 600;
  color: #303030;
`

const OrangeCircle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: #f5a623;
  border-radius: 50%;
  color: #fff;
  font-size: 16px;
`

const EMPTY_REFERRING = {
  elasticSelectValue: undefined,
  selectedRadiusAgentId: null,
  brokerageFirmName: "",
  brokerLicenseNumber: "",
  agentFirstName: "",
  agentLastName: "",
  agentLicenseNumber: "",
  address: "",
  email: "",
  phone: "",
}

const EMPTY_REPRESENTING = {
  elasticSelectValue: undefined,
  selectedRadiusAgentId: null,
  brokerageFirmName: "",
  brokerLicenseNumber: "",
  agentName: "",
  agentLicenseNumber: "",
  address: "",
  email: "",
  phone: "",
}

const EMPTY_CLIENT = {
  elasticSelectValue: undefined,
  selectedClientId: null,
  name: "",
  address: "",
  email: "",
  phone: "",
}

/** Merge street + optional apt/city/state/zip from referral-cover API into one line for the form. */
const brokerageAddressLineFromApi = (record) => {
  if (!record || typeof record !== "object") return ""
  const street = (
    record.brokerage_address
    || record.address
    || record.street
    || record.brokerage_street
    || ""
  ).trim()
  const apt = (record.brokerage_apt || record.apt || "").trim()
  const city = (record.brokerage_city || record.city || "").trim()
  const state = (record.brokerage_state || record.state || "").trim()
  const zip = String(record.brokerage_zipcode || record.zipcode || record.zip_code || "").trim()

  const line1 = [street, apt].filter(Boolean).join(" ").trim()
  const hasLocality = !!(city || state || zip)
  if (!hasLocality) return line1

  const streetLooksComplete = line1 && (
    (city && line1.includes(city))
    || (state && line1.includes(state))
    || (zip && line1.includes(zip))
  )
  if (streetLooksComplete) return line1

  const stateZip = [state, zip].filter(Boolean).join(" ").trim()
  const tail = [city, stateZip].filter(Boolean).join(", ")
  if (!line1) return tail
  return `${line1}, ${tail}`
}

/**
 * One-line brokerage address for referrals: street (+ apt), then city, state, zip.
 * Uses LOC first, then agent office / profile fields when LOC is sparse (common on elastic results).
 */
const fullRepresentingAddressFromLocAndAgent = (loc = {}, agent = {}) => {
  const street = (
    loc.brokerage_address
    || agent.office_street_address
    || agent.street_address
    || ""
  ).trim()
  const apt = (loc.apt || loc.brokerage_apt || agent.office_suite_no || "").trim()
  const city = (loc.city || agent.office_city || agent.user_city || "").trim()
  const state = (loc.state || agent.office_state || "").trim()
  const zip = String(loc.zipcode || agent.office_zipcode || agent.zipcode || "").trim()

  const line1 = [street, apt].filter(Boolean).join(" ").trim()
  const stateZip = [state, zip].filter(Boolean).join(" ").trim()
  const tail = [city, stateZip].filter(Boolean).join(", ").trim()

  if (line1 && tail) return `${line1}, ${tail}`
  if (line1) return line1
  if (tail) return tail
  return (agent.office_address || "").trim()
}

/** Ant Design drawer scrolls `.ant-drawer-body`, not the window; `scrollIntoView` often has no visible effect. */
const scrollElementIntoDrawerScrollParent = (el, behavior = "smooth") => {
  if (!el || typeof el.getBoundingClientRect !== "function") return
  let scrollRoot =
    el.closest(".ant-drawer-body")
    || el.closest(".ant-drawer-content")

  if (!scrollRoot) {
    let parent = el.parentElement
    while (parent && parent !== document.body) {
      const { overflowY } = window.getComputedStyle(parent)
      if (
        (overflowY === "auto" || overflowY === "scroll")
        && parent.scrollHeight > parent.clientHeight + 1
      ) {
        scrollRoot = parent
        break
      }
      parent = parent.parentElement
    }
  }

  if (!scrollRoot) {
    el.scrollIntoView({ behavior, block: "start" })
    return
  }

  const rootRect = scrollRoot.getBoundingClientRect()
  const elRect = el.getBoundingClientRect()
  const offset = 20
  const rawTop = scrollRoot.scrollTop + (elRect.top - rootRect.top) - offset
  const maxTop = Math.max(0, scrollRoot.scrollHeight - scrollRoot.clientHeight)
  scrollRoot.scrollTo({
    top: Math.max(0, Math.min(rawTop, maxTop)),
    behavior,
  })
}

const buildReferringFieldsFromAgent = (agent = {}, selectValue) => {
  const loc = agent.brokerage_info_from_LOC || {}
  const displayName = [agent.firstname, agent.lastname].filter(Boolean).join(" ").trim()
    || loc.name
    || ""

  let firstname = (agent.firstname || "").trim()
  let lastname = (agent.lastname || "").trim()
  if (!firstname && !lastname && displayName) {
    const nameParts = displayName.trim().split(/\s+/).filter(Boolean)
    firstname = nameParts[0] || ""
    lastname = nameParts.slice(1).join(" ") || ""
  }

  const brokerLicense = (
    loc.brokerage_license_number
    || agent.radius_brokerage_licence_number
    || agent.brokerage_license_number
    || ""
  )
  const agentLicense = (
    loc.agent_license_number
    || agent.real_estate_license_number
    || agent.user_license
    || ""
  )
  const agentId = parseInt(agent.id, 10)

  return {
    ...EMPTY_REFERRING,
    elasticSelectValue: selectValue || (agent.id ? `${agent.id}::${displayName}` : undefined),
    selectedRadiusAgentId: Number.isFinite(agentId) ? agentId : null,
    agentFirstName: firstname,
    agentLastName: lastname,
    brokerageFirmName: loc.listing_brokerage || loc.brokerage || agent.agency_name || "",
    brokerLicenseNumber: brokerLicense ? String(brokerLicense) : "",
    agentLicenseNumber: agentLicense ? String(agentLicense) : "",
    address: fullRepresentingAddressFromLocAndAgent(loc, agent),
    email: loc.email || agent.email || "",
    phone: formatPhoneNumber(loc.phone || agent.phone || ""),
  }
}

const EditDrawer = (props) => {
  const {
    isReferralDrawerOpen,
    currentReferralToEdit,
    toggleReferralDrawer,
    getReferralDetailsResponse,
    saveReferralCover,
    saveReferralCoverResponse,
    fetchAgentSearch,
    agents,
  } = props || {}

  const { data: details, isFetching } = getReferralDetailsResponse || {}
  const { isFetching: isSaving } = saveReferralCoverResponse || {}

  const [referring, setReferring] = useState({ ...EMPTY_REFERRING })
  const [representingAgents, setRepresentingAgents] = useState([{ ...EMPTY_REPRESENTING }])
  const [clients, setClients] = useState([{ ...EMPTY_CLIENT }])
  const [lastDetailsId, setLastDetailsId] = useState(null)
  const [contactErrors, setContactErrors] = useState(defaultReferralContactErrors())

  const repBlockRefs = useRef([])
  const clientBlockRefs = useRef([])
  const pendingRepScrollIndex = useRef(null)
  const pendingClientScrollIndex = useRef(null)
  const repAgentSearchDebounceRef = useRef(null)
  const referringAgentSearchDebounceRef = useRef(null)

  useEffect(() => {
    return () => {
      if (repAgentSearchDebounceRef.current) {
        window.clearTimeout(repAgentSearchDebounceRef.current)
      }
      if (referringAgentSearchDebounceRef.current) {
        window.clearTimeout(referringAgentSearchDebounceRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!isReferralDrawerOpen || !currentReferralToEdit?.isNew) return
    setReferring({ ...EMPTY_REFERRING })
    setRepresentingAgents([{ ...EMPTY_REPRESENTING }])
    setClients([{ ...EMPTY_CLIENT }])
    setLastDetailsId(null)
    setContactErrors(defaultReferralContactErrors())
  }, [isReferralDrawerOpen, currentReferralToEdit])

  useEffect(() => {
    if (!isReferralDrawerOpen || !currentReferralToEdit?.selectedReferringAgent) return
    setReferring(buildReferringFieldsFromAgent(
      currentReferralToEdit.selectedReferringAgent,
      currentReferralToEdit.referringAgentSelectValue,
    ))
    setContactErrors(prev => ({
      ...prev,
      referring: { email: "", phone: "" },
    }))
  }, [isReferralDrawerOpen, currentReferralToEdit])

  useEffect(() => {
    if (!isReferralDrawerOpen || !details || currentReferralToEdit?.isNew) return
    const detailId = details.metadata?.referral_id
      || details.property_info?.referral_id
      || details.referral_id
    if (!detailId || detailId === lastDetailsId) return
    setLastDetailsId(detailId)

    const splitFullName = (full) => {
      const parts = (full || "").trim().split(/\s+/).filter(Boolean)
      return {
        firstname: parts[0] || "",
        lastname: parts.slice(1).join(" ") || "",
      }
    }

    const refAgents = details.referring_agent_info || []
    const primary = refAgents.find(a => a.is_primary) || refAgents[0] || {}
    if (!refAgents.length && currentReferralToEdit?.selectedReferringAgent) {
      setReferring(buildReferringFieldsFromAgent(
        currentReferralToEdit.selectedReferringAgent,
        currentReferralToEdit.referringAgentSelectValue,
      ))
    } else {
      const primaryNames = primary.firstname || primary.lastname
        ? { firstname: primary.firstname || "", lastname: primary.lastname || "" }
        : splitFullName(primary.name)
      setReferring({
        elasticSelectValue: undefined,
        selectedRadiusAgentId: primary.agent_id != null ? Number(primary.agent_id) : null,
        brokerageFirmName: primary.brokerage || primary.brokerage_name || "",
        brokerLicenseNumber: primary.brokerage_license_number || primary.broker_license_number || "",
        agentFirstName: primaryNames.firstname,
        agentLastName: primaryNames.lastname,
        agentLicenseNumber: primary.agent_license_number || primary.license_number || "",
        address: brokerageAddressLineFromApi(primary),
        email: primary.email || "",
        phone: formatPhoneNumber(primary.phone || ""),
      })
    }

    const repAgents = details.representing_agent_info || []
    let nextRepCount = 1
    if (repAgents.length > 0) {
      nextRepCount = repAgents.length
      setRepresentingAgents(repAgents.map(a => ({
        elasticSelectValue: undefined,
        selectedRadiusAgentId: a.agent_id != null ? a.agent_id : null,
        brokerageFirmName: a.brokerage || a.brokerage_name || "",
        brokerLicenseNumber: a.brokerage_license_number || a.broker_license_number || "",
        agentName: a.name || [a.firstname, a.lastname].filter(Boolean).join(" "),
        agentLicenseNumber: a.agent_license_number || a.license_number || "",
        address: brokerageAddressLineFromApi(a),
        email: a.email || "",
        phone: formatPhoneNumber(a.phone || ""),
      })))
    } else {
      setRepresentingAgents([{ ...EMPTY_REPRESENTING }])
    }

    const clientList = details.client_info?.clients || []
    let nextClientCount = 1
    if (clientList.length > 0) {
      nextClientCount = clientList.length
      setClients(clientList.map(c => ({
        elasticSelectValue: undefined,
        selectedClientId: c.client_id != null ? c.client_id : null,
        name: c.name || [c.firstname, c.lastname].filter(Boolean).join(" "),
        address: c.address || "",
        email: c.email || "",
        phone: formatPhoneNumber(c.phone || ""),
      })))
    } else {
      setClients([{ ...EMPTY_CLIENT }])
    }

    setContactErrors({
      referring: { email: "", phone: "" },
      representing: Array.from({ length: nextRepCount }, () => ({ email: "", phone: "" })),
      clients: Array.from({ length: nextClientCount }, () => ({ email: "", phone: "" })),
    })
  }, [isReferralDrawerOpen, details, currentReferralToEdit?.isNew])

  useLayoutEffect(() => {
    const idx = pendingRepScrollIndex.current
    if (idx == null) return
    pendingRepScrollIndex.current = null
    const el = repBlockRefs.current[idx]
    const run = () => scrollElementIntoDrawerScrollParent(el, "smooth")
    run()
    const t1 = window.setTimeout(run, 80)
    const t2 = window.setTimeout(run, 280)
    return () => {
      window.clearTimeout(t1)
      window.clearTimeout(t2)
    }
  }, [representingAgents])

  useLayoutEffect(() => {
    const idx = pendingClientScrollIndex.current
    if (idx == null) return
    pendingClientScrollIndex.current = null
    const el = clientBlockRefs.current[idx]
    const run = () => scrollElementIntoDrawerScrollParent(el, "smooth")
    run()
    const t1 = window.setTimeout(run, 80)
    const t2 = window.setTimeout(run, 280)
    return () => {
      window.clearTimeout(t1)
      window.clearTimeout(t2)
    }
  }, [clients])

  const handleClose = () => {
    setReferring({ ...EMPTY_REFERRING })
    setRepresentingAgents([{ ...EMPTY_REPRESENTING }])
    setClients([{ ...EMPTY_CLIENT }])
    setLastDetailsId(null)
    setContactErrors(defaultReferralContactErrors())
    toggleReferralDrawer({ bool: false, data: null })
  }

  const updateReferring = (field, value) => {
    setReferring(prev => ({ ...prev, [field]: value }))
  }

  const updateRepAgent = (index, field, value) => {
    setRepresentingAgents(prev => {
      const next = [...prev]
      next[index] = { ...next[index], [field]: value }
      return next
    })
  }

  const scheduleRepresentingAgentSearch = (searchValue) => {
    if (repAgentSearchDebounceRef.current) {
      window.clearTimeout(repAgentSearchDebounceRef.current)
    }
    repAgentSearchDebounceRef.current = window.setTimeout(() => {
      repAgentSearchDebounceRef.current = null
      const q = (searchValue || "").trim()
      if (!q || !fetchAgentSearch) return
      fetchAgentSearch({ search: q })
    }, 500)
  }

  const scheduleReferringAgentSearch = (searchValue) => {
    if (referringAgentSearchDebounceRef.current) {
      window.clearTimeout(referringAgentSearchDebounceRef.current)
    }
    referringAgentSearchDebounceRef.current = window.setTimeout(() => {
      referringAgentSearchDebounceRef.current = null
      const q = (searchValue || "").trim()
      if (!q || !fetchAgentSearch) return
      fetchAgentSearch({ search: q })
    }, 500)
  }

  const handleReferringAgentPick = (value) => {
    if (!value || !agents?.data?.agents) return
    const parts = value.split("::")
    const agentId = parseInt(parts[0], 10)
    if (Number.isNaN(agentId)) return
    const agent = agents.data.agents.find(ag => ag.id === agentId)
    if (!agent) return

    setReferring(prev => ({
      ...prev,
      ...buildReferringFieldsFromAgent(agent, value),
    }))
    setContactErrors(prev => ({
      ...prev,
      referring: { email: "", phone: "" },
    }))
  }

  const handleRepresentingAgentPick = (index, value) => {
    if (!value || !agents?.data?.agents) return
    const parts = value.split("::")
    const agentId = parseInt(parts[0], 10)
    if (Number.isNaN(agentId)) return
    const agent = agents.data.agents.find(ag => ag.id === agentId)
    if (!agent) return
    const loc = agent.brokerage_info_from_LOC || {}
    const displayName = [agent.firstname, agent.lastname].filter(Boolean).join(" ").trim()
      || loc.name
      || parts[1]
      || ""

    const brokerLicense = (
      loc.brokerage_license_number
      || agent.radius_brokerage_licence_number
      || agent.brokerage_license_number
      || ""
    )
    const agentLicense = (
      loc.agent_license_number
      || agent.real_estate_license_number
      || agent.user_license
      || ""
    )
    const addressLine = fullRepresentingAddressFromLocAndAgent(loc, agent)

    setRepresentingAgents(prev => {
      const next = [...prev]
      next[index] = {
        ...next[index],
        elasticSelectValue: value,
        selectedRadiusAgentId: Number.isFinite(agentId) ? agentId : null,
        brokerageFirmName: loc.listing_brokerage || loc.brokerage || agent.agency_name || "",
        brokerLicenseNumber: brokerLicense ? String(brokerLicense) : "",
        agentName: loc.name || displayName,
        agentLicenseNumber: agentLicense ? String(agentLicense) : "",
        address: addressLine,
        email: loc.email || agent.email || "",
        phone: formatPhoneNumber(loc.phone || agent.phone || ""),
      }
      return next
    })
    setContactErrors(prev => {
      const rep = [...prev.representing]
      if (!rep[index]) return prev
      rep[index] = { email: "", phone: "" }
      return { ...prev, representing: rep }
    })
  }

  const addRepAgent = () => {
    setRepresentingAgents(prev => {
      const next = [...prev, { ...EMPTY_REPRESENTING }]
      pendingRepScrollIndex.current = next.length - 1
      return next
    })
    setContactErrors(prev => ({
      ...prev,
      representing: [...prev.representing, { email: "", phone: "" }],
    }))
  }

  const removeRepAgent = (index) => {
    setRepresentingAgents(prev => prev.filter((_, i) => i !== index))
    setContactErrors(prev => ({
      ...prev,
      representing: prev.representing.filter((_, i) => i !== index),
    }))
  }

  const updateClient = (index, field, value) => {
    setClients(prev => {
      const next = [...prev]
      next[index] = { ...next[index], [field]: value }
      return next
    })
  }

  const addClient = () => {
    setClients(prev => {
      const next = [...prev, { ...EMPTY_CLIENT }]
      pendingClientScrollIndex.current = next.length - 1
      return next
    })
    setContactErrors(prev => ({
      ...prev,
      clients: [...prev.clients, { email: "", phone: "" }],
    }))
  }

  const removeClient = (index) => {
    setClients(prev => prev.filter((_, i) => i !== index))
    setContactErrors(prev => ({
      ...prev,
      clients: prev.clients.filter((_, i) => i !== index),
    }))
  }

  const handleSave = () => {
    const validation = validateReferralContactRows({
      referring,
      representingAgents,
      clients,
    })
    if (validation.hasError) {
      setContactErrors(validation.errors)
      message.error("Please fix the email and phone errors before saving.")
      return
    }

    const referralId = currentReferralToEdit?.isNew
      ? null
      : (
        currentReferralToEdit?.referral_id
        || details?.metadata?.referral_id
        || details?.property_info?.referral_id
        || details?.referral_id
        || null
      )
    const isNew = !referralId || !!currentReferralToEdit?.isNew
    const agentId = currentReferralToEdit?.isNew
      ? (currentReferralToEdit?.primaryAgentId || currentReferralToEdit?.agent_id || null)
      : (
        details?.metadata?.agent_id
        || details?.property_info?.primary_agent_id
        || details?.agent_id
        || currentReferralToEdit?.primaryAgentId
        || null
      )

    const splitName = (full) => {
      const parts = (full || "").trim().split(/\s+/).filter(Boolean)
      return {
        firstname: parts[0] || "",
        lastname: parts.slice(1).join(" ") || "",
      }
    }

    const buildReferringAgent = () => ({
      is_primary: true,
      agent_id: agentId,
      name: [referring.agentFirstName, referring.agentLastName].filter(Boolean).join(" "),
      firstname: referring.agentFirstName || "",
      lastname: referring.agentLastName || "",
      email: referring.email || "",
      phone: phoneDigits(referring.phone),
      agent_license_number: referring.agentLicenseNumber || "",
      brokerage: referring.brokerageFirmName || "",
      brokerage_license_number: referring.brokerLicenseNumber || "",
      brokerage_address: referring.address || "",
      brokerage_apt: "",
      brokerage_city: "",
      brokerage_state: "",
      brokerage_zipcode: "",
    })

    const buildRepresentingAgents = () => representingAgents
      .filter(a => a.agentName || a.email || a.brokerageFirmName)
      .map((a, idx) => {
        const { firstname, lastname } = splitName(a.agentName)
        return {
          is_primary: idx === 0,
          agent_id: a.selectedRadiusAgentId != null ? a.selectedRadiusAgentId : null,
          name: a.agentName || "",
          firstname,
          lastname,
          email: a.email || "",
          phone: phoneDigits(a.phone),
          agent_license_number: a.agentLicenseNumber || "",
          brokerage: a.brokerageFirmName || "",
          brokerage_license_number: a.brokerLicenseNumber || "",
          brokerage_address: a.address || "",
          brokerage_apt: "",
          brokerage_city: "",
          brokerage_state: "",
          brokerage_zipcode: "",
        }
      })

    const buildClients = () => clients
      .filter(c => c.name || c.email)
      .map((c, idx) => {
        const { firstname, lastname } = splitName(c.name)
        return {
          is_primary: idx === 0,
          client_id: c.selectedClientId != null ? c.selectedClientId : null,
          req_id: null,
          transaction_id: null,
          firstname,
          lastname,
          name: c.name || "",
          email: c.email || "",
          phone: phoneDigits(c.phone),
          address: c.address || "",
          apt: "",
          city: "",
          state: "",
          zipcode: "",
          type_of_ownership: "individual",
          ownership_name: "",
        }
      })

    const coverPayload = {
      draft: true,
      current_referral_step: "referral_information",
      property_info: {
        state: null,
        street: null,
        apt: null,
        city: null,
        zipcode: null,
        county: null,
        mls_number: null,
        address: null,
      },
      referring_agent_info: [buildReferringAgent()],
      client_info: {
        clients: buildClients(),
        source: "other_agency",
        other_source: null,
      },
      representing_agent_info: buildRepresentingAgents(),
      vendors: {
        vendors: [],
        selected_vendor_id: null,
        contact_ids: [],
      },
      deal_details: {
        confirmed_closed_on: null,
        price: null,
        commission_type: null,
        commision_percentage: null,
        fixed_fee: null,
        commission_deductions_type: null,
        commission_deductions_percentage: null,
        commission_deductions_fixed_fee: null,
        referring_brokerage_name: null,
        miscellaneous_notes: null,
      },
    }

    const user = cookie.load("user") || {}
    const resolvedAgentId = agentId
      || (user.agent_id ? parseInt(user.agent_id, 10) : null)
      || (user.id ? parseInt(user.id, 10) : null)

    const primaryClient = clients.find(c => c.name || c.email) || null
    const primaryClientId = primaryClient
      ? (primaryClient.selectedClientId != null
        ? primaryClient.selectedClientId
        : primaryClient.client_id != null
          ? primaryClient.client_id
          : null)
      : null

    const createPayload = {
      agent_id: resolvedAgentId,
      draft: false,
      state: null,
      client_id: primaryClientId,
      client_source: "other_agency",
      client_type: "referral",
      is_direct_contract: false,
    }

    saveReferralCover({
      referralId,
      isNew,
      createPayload,
      coverPayload,
    })
  }

  const panelHeader = (icon, title) => (
    <SectionHeader>
      <SectionIndicator />
      <SectionIcon>{icon}</SectionIcon>
      <span>{title}</span>
    </SectionHeader>
  )

  const renderRepAgentExtra = (e) => {
    e.stopPropagation()
  }

  if (!isReferralDrawerOpen) return null

  return (
    <Drawer
      width={680}
      placement="right"
      onClose={handleClose}
      visible
      title={(
        <DrawerTitle>
          <OrangeCircle>&#9658;</OrangeCircle>
          Referral Details
        </DrawerTitle>
      )}
      destroyOnClose
    >
      {isFetching ? (
        <div style={{ textAlign: "center", paddingTop: 80 }}>
          <Spin size="large" />
        </div>
      ) : (
        <DrawerBody>
          <Collapse
            defaultActiveKey={["referring", "representing", "client"]}
            expandIconPosition="end"
          >
            {/* ── Referring Broker/Agent Information ── */}
            <Panel
              key="referring"
              header={panelHeader(<UserOutlined />, "Referring Broker/Agent Information")}
            >
              <FieldRow>
                <FieldGroup>
                  <FieldLabel>Referring Broker (Brokerage Firm Name)</FieldLabel>
                  <Input
                    placeholder="Enter brokerage name"
                    value={referring.brokerageFirmName}
                    onChange={e => updateReferring("brokerageFirmName", e.target.value)}
                  />
                </FieldGroup>
                <FieldGroup>
                  <FieldLabel>Referring Broker License #</FieldLabel>
                  <Input
                    placeholder="Enter license number"
                    value={referring.brokerLicenseNumber}
                    onChange={e => updateReferring("brokerLicenseNumber", e.target.value)}
                  />
                </FieldGroup>
              </FieldRow>

              <SearchRow>
                <Select
                  showSearch
                  defaultActiveFirstOption={false}
                  showArrow={false}
                  filterOption={false}
                  placeholder="Search by agent name or email address"
                  style={{ width: "100%" }}
                  value={referring.elasticSelectValue}
                  onSearch={scheduleReferringAgentSearch}
                  onChange={handleReferringAgentPick}
                  notFoundContent={(agents?.isFetching || agents?.isFetchingMore) ? (
                    <Spin size="large" indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                  ) : null}
                  options={
                    (agents && agents.data && agents.data.agents)
                      ? agents.data.agents.map(d => ({
                        label: `${d.firstname} ${d.lastname} - ${d.email}`,
                        value: `${d.id}::${d.firstname} ${d.lastname}`,
                      }))
                      : []
                  }
                />
              </SearchRow>
              <InfoText>
                &#9432; If agent is part of Radius, you can search by agent name. Select an agent from the list.
              </InfoText>

              <FieldRow>
                <FieldGroup>
                  <FieldLabel>Referring Agent</FieldLabel>
                  <Input
                    placeholder="Enter agent name"
                    value={referring.agentFirstName}
                    onChange={e => updateReferring("agentFirstName", e.target.value)}
                  />
                </FieldGroup>
                <FieldGroup>
                  <FieldLabel>&nbsp;</FieldLabel>
                  <Input
                    placeholder=""
                    value={referring.agentLastName}
                    onChange={e => updateReferring("agentLastName", e.target.value)}
                  />
                </FieldGroup>
              </FieldRow>

              <FieldRow>
                <FieldGroup>
                  <FieldLabel>Referring Agent License #</FieldLabel>
                  <Input
                    placeholder="Enter license number"
                    value={referring.agentLicenseNumber}
                    onChange={e => updateReferring("agentLicenseNumber", e.target.value)}
                  />
                </FieldGroup>
                <FieldGroup />
              </FieldRow>

              <FullWidthField>
                <FieldLabel>Address Of Referring Brokerage</FieldLabel>
                <GoogleAutoComplete
                  id={`referring-address`}
                  types={["address"]}
                  name="referring-address"
                  value={referring.address}
                  onChange={e => updateReferring("address", e.target.value)}
                  location={(loc) => { updateReferring("address", loc?.address || "") }}
                  placeholder="Street Address"
                />
              </FullWidthField>

              <FieldRow>
                <FieldGroup>
                  <FieldLabel>Referring Agent Email Address</FieldLabel>
                  <Form.Item
                    style={{ marginBottom: 0 }}
                    validateStatus={contactErrors.referring.email ? "error" : undefined}
                    help={contactErrors.referring.email || undefined}
                  >
                    <Input
                      placeholder="Enter email address"
                      value={referring.email}
                      onChange={(e) => {
                        updateReferring("email", e.target.value)
                        if (contactErrors.referring.email) {
                          setContactErrors(prev => ({
                            ...prev,
                            referring: { ...prev.referring, email: "" },
                          }))
                        }
                      }}
                    />
                  </Form.Item>
                </FieldGroup>
                <FieldGroup>
                  <FieldLabel>Referring Agent Phone Number</FieldLabel>
                  <Form.Item
                    style={{ marginBottom: 0 }}
                    validateStatus={contactErrors.referring.phone ? "error" : undefined}
                    help={contactErrors.referring.phone || undefined}
                  >
                    <Input
                      placeholder="000-000-0000"
                      value={referring.phone}
                      onChange={(e) => {
                        updateReferring("phone", formatPhoneNumber(e.target.value))
                        if (contactErrors.referring.phone) {
                          setContactErrors(prev => ({
                            ...prev,
                            referring: { ...prev.referring, phone: "" },
                          }))
                        }
                      }}
                    />
                  </Form.Item>
                </FieldGroup>
              </FieldRow>
            </Panel>

            {/* ── Representing Broker/Agent ── */}
            <Panel
              key="representing"
              header={panelHeader(<UserOutlined />, "Representing Broker/Agent")}
              extra={(
                <AddButton
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={(e) => { e.stopPropagation(); addRepAgent() }}
                >
                  Representing Broker/Agent
                </AddButton>
              )}
            >
              {representingAgents.map((agent, idx) => (
                <div
                  key={idx}
                  ref={(el) => { repBlockRefs.current[idx] = el }}
                  style={{ scrollMarginTop: 16 }}
                >
                  <RepAgentBlock>
                  {representingAgents.length > 1 && (
                    <RepAgentTitle>
                      <RepAgentLabel>Representing Agent {idx + 1}</RepAgentLabel>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                        onClick={() => removeRepAgent(idx)}
                      />
                    </RepAgentTitle>
                  )}

                  <SearchRow>
                    <Select
                      showSearch
                      defaultActiveFirstOption={false}
                      showArrow={false}
                      filterOption={false}
                      placeholder="Search by agent name or email address"
                      style={{ width: "100%" }}
                      value={agent.elasticSelectValue}
                      onSearch={scheduleRepresentingAgentSearch}
                      onChange={val => handleRepresentingAgentPick(idx, val)}
                      notFoundContent={(agents?.isFetching || agents?.isFetchingMore) ? (
                        <Spin size="large" indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                      ) : null}
                      options={
                        (agents && agents.data && agents.data.agents)
                          ? agents.data.agents.map(d => ({
                            label: `${d.firstname} ${d.lastname} - ${d.email}`,
                            value: `${d.id}::${d.firstname} ${d.lastname}`,
                          }))
                          : []
                      }
                    />
                  </SearchRow>
                  <InfoText>
                    &#9432; If agent is part of Radius, you can search by agent name. Select an agent from the list.
                  </InfoText>

                  <FieldRow>
                    <FieldGroup>
                      <FieldLabel>Representing Broker (Brokerage Firm Name)</FieldLabel>
                      <Input
                        placeholder="Enter brokerage name"
                        value={agent.brokerageFirmName}
                        onChange={e => updateRepAgent(idx, "brokerageFirmName", e.target.value)}
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <FieldLabel>Representing Broker License #</FieldLabel>
                      <Input
                        placeholder="Enter license number"
                        value={agent.brokerLicenseNumber}
                        onChange={e => updateRepAgent(idx, "brokerLicenseNumber", e.target.value)}
                      />
                    </FieldGroup>
                  </FieldRow>

                  <FieldRow>
                    <FieldGroup>
                      <FieldLabel>Representing Agent</FieldLabel>
                      <Input
                        placeholder="Enter agent name"
                        value={agent.agentName}
                        onChange={e => updateRepAgent(idx, "agentName", e.target.value)}
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <FieldLabel>Representing Agent License #</FieldLabel>
                      <Input
                        placeholder="Enter license number"
                        value={agent.agentLicenseNumber}
                        onChange={e => updateRepAgent(idx, "agentLicenseNumber", e.target.value)}
                      />
                    </FieldGroup>
                  </FieldRow>

                  <FullWidthField>
                    <FieldLabel>Address Of Representing Brokerage</FieldLabel>
                    <GoogleAutoComplete
                      id={`representing-address-${idx}`}
                      types={["address"]}
                      name={`representing-address-${idx}`}
                      value={agent.address}
                      onChange={e => updateRepAgent(idx, "address", e.target.value)}
                      location={(loc) => { updateRepAgent(idx, "address", loc?.address || "") }}
                      placeholder="Street Address"
                    />
                  </FullWidthField>

                  <FieldRow>
                    <FieldGroup>
                      <FieldLabel>Representing Brokerage Email Address</FieldLabel>
                      <Form.Item
                        style={{ marginBottom: 0 }}
                        validateStatus={contactErrors.representing[idx]?.email ? "error" : undefined}
                        help={contactErrors.representing[idx]?.email || undefined}
                      >
                        <Input
                          placeholder="Enter email address"
                          value={agent.email}
                          onChange={(e) => {
                            updateRepAgent(idx, "email", e.target.value)
                            if (contactErrors.representing[idx] && contactErrors.representing[idx].email) {
                              setContactErrors(prev => {
                                const next = { ...prev, representing: [...prev.representing] }
                                next.representing[idx] = { ...next.representing[idx], email: "" }
                                return next
                              })
                            }
                          }}
                        />
                      </Form.Item>
                    </FieldGroup>
                    <FieldGroup>
                      <FieldLabel>Representing Brokerage Phone Number</FieldLabel>
                      <Form.Item
                        style={{ marginBottom: 0 }}
                        validateStatus={contactErrors.representing[idx]?.phone ? "error" : undefined}
                        help={contactErrors.representing[idx]?.phone || undefined}
                      >
                        <Input
                          placeholder="000-000-0000"
                          value={agent.phone}
                          onChange={(e) => {
                            updateRepAgent(idx, "phone", formatPhoneNumber(e.target.value))
                            if (contactErrors.representing[idx] && contactErrors.representing[idx].phone) {
                              setContactErrors(prev => {
                                const next = { ...prev, representing: [...prev.representing] }
                                next.representing[idx] = { ...next.representing[idx], phone: "" }
                                return next
                              })
                            }
                          }}
                        />
                      </Form.Item>
                    </FieldGroup>
                  </FieldRow>
                  </RepAgentBlock>
                </div>
              ))}
            </Panel>

            {/* ── Client Information ── */}
            <Panel
              key="client"
              header={panelHeader(<UserOutlined />, "Client Information")}
              extra={(
                <AddButton
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={(e) => { e.stopPropagation(); addClient() }}
                >
                  Client
                </AddButton>
              )}
            >
              {clients.map((client, idx) => (
                <div
                  key={idx}
                  ref={(el) => { clientBlockRefs.current[idx] = el }}
                  style={{ scrollMarginTop: 16 }}
                >
                  <ClientBlock>
                  {clients.length > 1 && (
                    <ClientTitle>
                      <ClientLabel>Client {idx + 1}</ClientLabel>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                        onClick={() => removeClient(idx)}
                      />
                    </ClientTitle>
                  )}

                  {/*
                    Client search (/v2.0/clients/search + Select) — disabled for now; ship later.
                    Restore: import searchClientAPI, clientSearchByRow state + debounce timers,
                    scheduleClientSearch / handleClientPick, cleanup in effects + add/remove client,
                    SearchRow Select + InfoText block.
                  */}

                  <FieldRow>
                    <FieldGroup>
                      <FieldLabel>Client Name</FieldLabel>
                      <Input
                        placeholder="Enter name"
                        value={client.name}
                        onChange={e => updateClient(idx, "name", e.target.value)}
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <FieldLabel>Address</FieldLabel>
                      <GoogleAutoComplete
                        id={`client-address-${idx}`}
                        types={["address"]}
                        name={`client-address-${idx}`}
                        value={client.address}
                        onChange={e => updateClient(idx, "address", e.target.value)}
                        location={(loc) => { updateClient(idx, "address", loc?.address || "") }}
                        placeholder="Street Address"
                      />
                    </FieldGroup>
                  </FieldRow>

                  <FieldRow>
                    <FieldGroup>
                      <FieldLabel>Email</FieldLabel>
                      <Form.Item
                        style={{ marginBottom: 0 }}
                        validateStatus={contactErrors.clients[idx]?.email ? "error" : undefined}
                        help={contactErrors.clients[idx]?.email || undefined}
                      >
                        <Input
                          placeholder="Enter your email"
                          value={client.email}
                          onChange={(e) => {
                            updateClient(idx, "email", e.target.value)
                            if (contactErrors.clients[idx] && contactErrors.clients[idx].email) {
                              setContactErrors(prev => {
                                const next = { ...prev, clients: [...prev.clients] }
                                next.clients[idx] = { ...next.clients[idx], email: "" }
                                return next
                              })
                            }
                          }}
                        />
                      </Form.Item>
                    </FieldGroup>
                    <FieldGroup>
                      <FieldLabel>Phone Number</FieldLabel>
                      <Form.Item
                        style={{ marginBottom: 0 }}
                        validateStatus={contactErrors.clients[idx]?.phone ? "error" : undefined}
                        help={contactErrors.clients[idx]?.phone || undefined}
                      >
                        <Input
                          placeholder="000-000-0000"
                          value={client.phone}
                          onChange={(e) => {
                            updateClient(idx, "phone", formatPhoneNumber(e.target.value))
                            if (contactErrors.clients[idx] && contactErrors.clients[idx].phone) {
                              setContactErrors(prev => {
                                const next = { ...prev, clients: [...prev.clients] }
                                next.clients[idx] = { ...next.clients[idx], phone: "" }
                                return next
                              })
                            }
                          }}
                        />
                      </Form.Item>
                    </FieldGroup>
                  </FieldRow>
                  </ClientBlock>
                </div>
              ))}
            </Panel>
          </Collapse>

          <FooterWrap>
            <SaveButton type="primary" onClick={handleSave} loading={isSaving}>
              Save &amp; Proceed
            </SaveButton>
          </FooterWrap>
        </DrawerBody>
      )}
    </Drawer>
  )
}

export default withRouter(ReferralDashboardContainer(agentSearchContainer(EditDrawer)))
